import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Session, SupabaseClient, User } from '@supabase/supabase-js';
import { Dialog } from './ui/dialog';
import { DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { DialogHeader } from './ui/dialog';
import { LogoColor } from './Logo';
import { getPlans } from '@/config/pricing';
import { cn } from '@/utils';

export type AuthView = 'sign_in' | 'sign_up' | 'forgotten_password' | 'magic_link' | 'update_password';

const FORM_VIEWS: AuthView[] = ['sign_in', 'sign_up', 'forgotten_password', 'update_password'];

/** Maps anything unexpected (including the retired magic_link view) to sign in. */
export function normalizeAuthView(view: string | null | undefined): AuthView {
  return FORM_VIEWS.includes(view as AuthView) ? (view as AuthView) : 'sign_in';
}

const APP_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://svg.new';

/**
 * Turns a caller's redirectTo into the absolute URL Supabase needs. Callers pass a
 * path ("/pricing?checkout=pro"); some pass an absolute URL already, which is kept.
 */
export function resolveRedirectUrl(redirectTo?: string): string | undefined {
  if (!redirectTo) return undefined;
  if (/^https?:\/\//i.test(redirectTo)) return redirectTo;
  return `${APP_URL}${redirectTo.startsWith('/') ? '' : '/'}${redirectTo}`;
}

/**
 * Only same-site paths are honoured. "//evil.com" and "/\evil.com" also start with "/"
 * but browsers treat them as protocol-relative URLs to another host, and browsers drop
 * tabs and newlines from URLs, so "/<tab>/evil.com" is rejected too.
 */
export function safeRedirectPath(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (!/^\/(?!\/|\\)/.test(value)) return undefined;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(value)) return undefined;
  return value;
}

/** Where a password-reset email link lands: the new-password form, then back to redirectTo. */
export function recoveryRedirectUrl(redirectTo?: string): string {
  let next = redirectTo;
  if (next && next.startsWith(APP_URL)) next = next.slice(APP_URL.length) || '/';
  const safeNext = safeRedirectPath(next);
  const path = `/login?view=update_password${safeNext ? `&redirect=${encodeURIComponent(safeNext)}` : ''}`;
  return `${APP_URL}${path}`;
}

/** Plain-language copy for the errors gotrue returns. */
export function friendlyAuthError(message?: string | null, status?: number): string {
  const m = (message || '').toLowerCase();
  if (m.includes('invalid login credentials')) {
    return "That email and password don't match. Try again or reset your password.";
  }
  if (m.includes('already registered') || m.includes('already been registered') || m.includes('already exists')) {
    return 'There is already an account with that email. Sign in instead.';
  }
  if (m.includes('email not confirmed')) {
    return 'Confirm your email first. Check your inbox for the link we sent.';
  }
  if (
    status === 429 ||
    m.includes('rate limit') ||
    m.includes('too many') ||
    m.includes('only request this after') ||
    m.includes('for security purposes')
  ) {
    return 'Too many attempts. Wait a minute and try again.';
  }
  const minLength = m.match(/password should be at least (\d+)/);
  if (minLength) return `Use at least ${minLength[1]} characters for your password.`;
  if (m.includes('should be different')) return 'Choose a password you have not used before.';
  if (m.includes('must provide either an email')) return 'Enter your email.';
  if (m.includes('unable to validate email') || m.includes('invalid email') || m.includes('valid email')) {
    return 'Enter a valid email address.';
  }
  if (m.includes('auth session missing') || m.includes('not logged in') || m.includes('invalid jwt') || m.includes('expired')) {
    return 'Your reset link has expired. Request a new one.';
  }
  if (m.includes('failed to fetch') || m.includes('network') || m.includes('request failed')) {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  if (message && message.length <= 140) return message;
  return 'Something went wrong. Try again.';
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD = 6;

type FieldErrors = { email?: string; password?: string };

/** Client-side checks that run before anything is sent to Supabase. */
export function validateAuthForm(view: AuthView, email: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  const needsEmail = view !== 'update_password';
  const needsPassword = view !== 'forgotten_password';
  if (needsEmail) {
    if (!email.trim()) errors.email = 'Enter your email';
    else if (!EMAIL_RE.test(email.trim())) errors.email = 'Enter a valid email address';
  }
  if (needsPassword) {
    if (!password) errors.password = view === 'update_password' ? 'Enter a new password' : 'Enter your password';
    else if ((view === 'sign_up' || view === 'update_password') && password.length < MIN_PASSWORD) {
      errors.password = `Use at least ${MIN_PASSWORD} characters`;
    }
  }
  return errors;
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

const inputCls =
  'w-full h-10 px-3 rounded border bg-paper text-[14px] text-ink placeholder:text-ink-faint outline-none transition-colors focus:border-sky focus:ring-[3px] focus:ring-sky-ring';
const linkCls = 'text-[13px] text-ink-muted hover:text-sky transition-colors';

const SUBMIT_LABELS: Record<AuthView, [string, string]> = {
  sign_in: ['Sign in', 'Signing in…'],
  sign_up: ['Create account', 'Creating account…'],
  forgotten_password: ['Send reset link', 'Sending…'],
  magic_link: ['Sign in', 'Signing in…'],
  update_password: ['Update password', 'Updating…'],
};

// Our own forms rather than the @supabase/ui <Auth> widget: the widget remounted its
// inputs on every parent render (losing the first click after a view switch), showed
// raw gotrue errors, had no required fields and no client-side checks, and couldn't
// point password-reset links at the new-password form.
export function AuthBasic(props: {
  authView: string;
  setAuthView: (view: string) => void;
  supabase: SupabaseClient;
  onSignedIn: (user: User) => void;
  /** Path (or absolute URL) to come back to after Google or an email link. */
  redirectTo?: string;
  /** Called on every view change, including forgotten/update password. */
  onViewChange?: (view: string) => void;
}) {
  const { supabase, authView, redirectTo } = props;
  const uid = useId();

  // The view being shown. Callers only hear about sign_in/sign_up through
  // setAuthView (some map it to a boolean); every change goes to onViewChange.
  const [view, setView] = useState<AuthView>(() => normalizeAuthView(authView));
  const viewRef = useRef(view);
  const onViewChangeRef = useRef(props.onViewChange);
  onViewChangeRef.current = props.onViewChange;
  const onSignedInRef = useRef(props.onSignedIn);
  onSignedInRef.current = props.onSignedIn;
  const setAuthViewRef = useRef(props.setAuthView);
  setAuthViewRef.current = props.setAuthView;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resetExpired, setResetExpired] = useState(false);

  const showView = useCallback((next: AuthView) => {
    viewRef.current = next;
    setView(next);
    setFieldErrors({});
    setFormError(null);
    setNotice(null);
    setResetExpired(false);
    if (next === 'update_password' || next === 'forgotten_password') setPassword('');
  }, []);

  // Follow the caller's view (dialog reopened, ?view= changed).
  useEffect(() => {
    const next = normalizeAuthView(authView);
    if (next !== viewRef.current) showView(next);
  }, [authView, showView]);

  const changeView = useCallback(
    (next: AuthView) => {
      showView(next);
      onViewChangeRef.current?.(next);
      if (next === 'sign_in' || next === 'sign_up') setAuthViewRef.current(next);
    },
    [showView]
  );

  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const pendingSession = useRef<Session | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  // True while a password-reset link's session is being used to pick a new password;
  // that sign-in must not trigger the caller's onSignedIn (which navigates away).
  const recoveryRef = useRef(false);
  useEffect(() => {
    if (typeof window !== 'undefined' && /(^|[#&])type=recovery(&|$)/.test(window.location.hash)) {
      recoveryRef.current = true;
    }
  }, []);

  // Post the session to /api/auth so the server-side cookie is set, and only then
  // report the sign-in. A failed sync leaves the user half signed in (client yes,
  // server no), so it surfaces an error with a retry instead of continuing.
  const syncSignedIn = useCallback(async (session: Session) => {
    pendingSession.current = session;
    setSyncing(true);
    setSyncError(null);
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: new Headers({ 'Content-Type': 'application/json' }),
        credentials: 'same-origin',
        body: JSON.stringify({ event: 'SIGNED_IN', session }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      pendingSession.current = null;
      if (recoveryRef.current || viewRef.current === 'update_password') return;
      if (session.user) onSignedInRef.current?.(session.user);
    } catch {
      setSyncError("You're signed in, but we couldn't start your session.");
    } finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    // Other events (sign-out, token refresh) are synced to the cookie by _app.
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoveryRef.current = true;
        changeView('update_password');
        return;
      }
      if (event === 'SIGNED_IN' && session?.user) syncSignedIn(session);
    });
    return () => {
      authListener?.unsubscribe();
    };
  }, [supabase, syncSignedIn, changeView]);

  const finalRedirectTo = resolveRedirectUrl(redirectTo);

  const handleGoogle = async () => {
    setGoogleError(null);
    setGoogleLoading(true);
    const { error } = await supabase.auth.signIn({ provider: 'google' }, { redirectTo: finalRedirectTo });
    if (error) {
      setGoogleError(friendlyAuthError(error.message, error.status));
      setGoogleLoading(false);
    }
    // On success the browser navigates to Google, so loading stays on.
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const current = viewRef.current;
    setFormError(null);
    setNotice(null);
    setResetExpired(false);
    const errors = validateAuthForm(current, email, password);
    setFieldErrors(errors);
    if (errors.email || errors.password) {
      const form = e.currentTarget;
      const first = form.querySelector<HTMLInputElement>(errors.email ? 'input[name="email"]' : 'input[name="password"]');
      first?.focus();
      return;
    }

    const cleanEmail = email.trim();
    setSubmitting(true);
    try {
      if (current === 'sign_in') {
        const { error } = await supabase.auth.signIn({ email: cleanEmail, password }, { redirectTo: finalRedirectTo });
        if (error) setFormError(friendlyAuthError(error.message, error.status));
        // Success fires SIGNED_IN, which syncSignedIn handles.
      } else if (current === 'sign_up') {
        const { user, session, error } = await supabase.auth.signUp(
          { email: cleanEmail, password },
          { redirectTo: finalRedirectTo }
        );
        if (error) {
          setFormError(friendlyAuthError(error.message, error.status));
        } else if (user && !session) {
          // gotrue answers an existing, confirmed address with a user that has no
          // identities instead of an error.
          if (Array.isArray(user.identities) && user.identities.length === 0) {
            setFormError(friendlyAuthError('User already registered'));
          } else {
            setNotice(`Check ${cleanEmail} for a confirmation link to finish signing up.`);
          }
        }
      } else if (current === 'forgotten_password') {
        const { error } = await supabase.auth.api.resetPasswordForEmail(cleanEmail, {
          redirectTo: recoveryRedirectUrl(redirectTo),
        });
        if (error) setFormError(friendlyAuthError(error.message, error.status));
        else setNotice(`If ${cleanEmail} has an account, a link to reset your password is on its way.`);
      } else if (current === 'update_password') {
        if (!supabase.auth.session()) {
          setFormError('Your reset link has expired or was already used. Request a new one.');
          setResetExpired(true);
          return;
        }
        const { user, error } = await supabase.auth.update({ password });
        if (error) {
          const friendly = friendlyAuthError(error.message, error.status);
          setFormError(friendly);
          if (friendly.startsWith('Your reset link')) setResetExpired(true);
        } else {
          recoveryRef.current = false;
          setPassword('');
          setNotice("Password updated. You're signed in.");
          if (user) setTimeout(() => onSignedInRef.current?.(user), 1200);
        }
      }
    } catch (err) {
      setFormError(friendlyAuthError(err instanceof Error ? err.message : null));
    } finally {
      setSubmitting(false);
    }
  };

  const showSocial = view === 'sign_in' || view === 'sign_up';
  const needsEmail = view !== 'update_password';
  const needsPassword = view !== 'forgotten_password';
  const [submitLabel, busyLabel] = SUBMIT_LABELS[view];
  const busy = submitting || syncing;
  const emailId = `${uid}-email`;
  const passwordId = `${uid}-password`;

  return (
    <div className="w-full">
      {syncError && (
        <div role="alert" className="mb-5 rounded border border-cream-300 bg-cream-100 px-3 py-2.5">
          <p className="text-[13px] text-ink">{syncError}</p>
          <button
            type="button"
            disabled={syncing}
            onClick={() => pendingSession.current && syncSignedIn(pendingSession.current)}
            className="mt-2 inline-flex items-center h-7 px-2.5 rounded text-[13px] font-medium bg-sky text-sky-ink hover:bg-sky-soft disabled:opacity-60 transition-colors"
          >
            {syncing ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}

      {showSocial && (
        <div className="mb-6">
          <button
            type="button"
            onClick={handleGoogle}
            disabled={googleLoading}
            className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded border border-cream-300 bg-white text-[14px] font-medium text-ink-soft hover:bg-cream-100 hover:border-cream-400 disabled:opacity-60 transition-colors"
          >
            <GoogleIcon />
            {googleLoading
              ? 'Opening Google…'
              : view === 'sign_in'
                ? 'Sign in with Google'
                : 'Sign up with Google'}
          </button>
          {googleError && (
            <p role="alert" className="mt-2 text-[12px] text-clay">
              {googleError}
            </p>
          )}
          <div className="mt-6 flex items-center gap-3 text-[12px] text-ink-muted">
            <span className="h-px flex-1 bg-cream-200" />
            <span>or use email</span>
            <span className="h-px flex-1 bg-cream-200" />
          </div>
        </div>
      )}

      <form id={`auth-${view.replace(/_/g, '-')}`} onSubmit={handleSubmit} noValidate className="space-y-4">
        {needsEmail && (
          <div>
            <label htmlFor={emailId} className="block mb-1.5 text-[13px] text-ink-muted">
              Email address
            </label>
            <input
              id={emailId}
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.email}
              aria-describedby={fieldErrors.email ? `${emailId}-error` : undefined}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldErrors.email) setFieldErrors((f) => ({ ...f, email: undefined }));
              }}
              className={cn(inputCls, fieldErrors.email ? 'border-clay' : 'border-cream-300')}
            />
            {fieldErrors.email && (
              <p id={`${emailId}-error`} className="mt-1.5 text-[12px] text-clay">
                {fieldErrors.email}
              </p>
            )}
          </div>
        )}

        {needsPassword && (
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label htmlFor={passwordId} className="block text-[13px] text-ink-muted">
                {view === 'update_password' ? 'New password' : 'Password'}
              </label>
              {view === 'sign_in' && (
                <button type="button" onClick={() => changeView('forgotten_password')} className={linkCls}>
                  Forgot your password?
                </button>
              )}
            </div>
            <input
              id={passwordId}
              name="password"
              type="password"
              autoComplete={view === 'sign_in' ? 'current-password' : 'new-password'}
              required
              aria-required="true"
              minLength={view === 'sign_in' ? undefined : MIN_PASSWORD}
              aria-invalid={!!fieldErrors.password}
              aria-describedby={fieldErrors.password ? `${passwordId}-error` : undefined}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (fieldErrors.password) setFieldErrors((f) => ({ ...f, password: undefined }));
              }}
              className={cn(inputCls, fieldErrors.password ? 'border-clay' : 'border-cream-300')}
            />
            {fieldErrors.password && (
              <p id={`${passwordId}-error`} className="mt-1.5 text-[12px] text-clay">
                {fieldErrors.password}
              </p>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          aria-busy={busy}
          className="w-full inline-flex items-center justify-center h-10 px-4 rounded bg-sky text-sky-ink text-[14px] font-medium hover:bg-sky-soft disabled:opacity-60 disabled:cursor-wait transition-colors"
        >
          {busy ? busyLabel : submitLabel}
        </button>

        {formError && (
          <div role="alert" className="text-[13px] leading-relaxed text-clay">
            <p>{formError}</p>
            {resetExpired && (
              <button type="button" onClick={() => changeView('forgotten_password')} className="mt-1 underline hover:text-ink">
                Send a new reset link
              </button>
            )}
          </div>
        )}
        {notice && (
          <p role="status" className="text-[13px] leading-relaxed text-ink-soft">
            {notice}
          </p>
        )}
      </form>

      <div className="mt-5 text-center">
        {view === 'sign_in' && (
          <button type="button" onClick={() => changeView('sign_up')} className={linkCls}>
            Don&apos;t have an account? Sign up
          </button>
        )}
        {view === 'sign_up' && (
          <button type="button" onClick={() => changeView('sign_in')} className={linkCls}>
            Already have an account? Sign in
          </button>
        )}
        {(view === 'forgotten_password' || view === 'update_password') && (
          <button type="button" onClick={() => changeView('sign_in')} className={linkCls}>
            Back to sign in
          </button>
        )}
      </div>
    </div>
  );
}

const DEFAULT_TITLES: Record<AuthView, string> = {
  sign_in: 'Sign in',
  sign_up: 'Create your free account',
  forgotten_password: 'Reset your password',
  magic_link: 'Sign in with a link',
  update_password: 'Choose a new password',
};

function defaultDescription(view: AuthView): string {
  if (view === 'sign_up') {
    const free = getPlans(null).find((p) => p.name === 'Free');
    return `Free includes ${free?.features[0].toLowerCase() ?? 'daily vectorizations'}. No card needed.`;
  }
  if (view === 'forgotten_password') return "We'll email you a link to choose a new password.";
  if (view === 'update_password') return "Use at least 6 characters. You'll stay signed in.";
  return 'Welcome back. Use Google or your email.';
}

export default function LoginDialog(props: {
  open: boolean;
  supabase: SupabaseClient;
  onSignedIn: (user: User) => void;
  onClose: () => void;
  isSignIn?: boolean;
  redirectTo?: string;
  title?: string;
  description?: string;
}) {
  const { supabase, onSignedIn, onClose, open, isSignIn = false, redirectTo, title, description } = props;
  const [authView, setAuthView] = useState<AuthView>(isSignIn ? 'sign_in' : 'sign_up');

  // Reset to the requested view whenever the dialog opens or the caller changes isSignIn.
  useEffect(() => {
    if (open) setAuthView(isSignIn ? 'sign_in' : 'sign_up');
  }, [open, isSignIn]);

  const isEntryView = authView === 'sign_in' || authView === 'sign_up';
  const heading = (isEntryView && title) || DEFAULT_TITLES[authView] || DEFAULT_TITLES.sign_in;
  const subheading = (isEntryView && description) || defaultDescription(authView);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[480px] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-md bg-white text-ink border border-cream-300 px-6 py-8 sm:px-12 sm:py-10 shadow-[0_24px_60px_-20px_rgba(30,33,38,0.35)] focus:outline-none focus-visible:outline-none">
        <DialogHeader className="items-center text-center sm:text-center space-y-0">
          <div aria-hidden="true" className="mb-6">
            <LogoColor className="w-[128px] cursor-default" />
          </div>
          <DialogTitle className="text-[22px] sm:text-[24px] font-semibold tracking-[-0.02em] text-ink leading-[1.15]">
            {heading}
          </DialogTitle>
          <DialogDescription className="mt-2 text-[14px] text-ink-muted max-w-[340px]">
            {subheading}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <AuthBasic
            authView={authView}
            setAuthView={(view) => setAuthView(view as AuthView)}
            onViewChange={(view) => setAuthView(view as AuthView)}
            supabase={supabase}
            onSignedIn={onSignedIn}
            redirectTo={redirectTo}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
